import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Layout } from "../layout/Layout";
import { Sidebar } from "../components/SideBar";
import { ProviderMainCard } from "../components/Profesional/ProviderMainCard";
import { ProviderDetails } from "../components/Profesional/ProviderDetails";
import { ProviderSideCard } from "../components/Profesional/ProviderSideCard";
import OrderListModal, {
	type OrderListService,
	type OrderListSubmitPayload,
	type AddressSummary,
	type NewAddressPayload,
	type ServiceContextForm,
} from "../components/modals/clientmodals/orderlist";
import { AlertModal, type AlertVariant } from "../components/modals/AlertModal";
import { ConfirmModal } from "../components/modals/ConfirmModal";
import { ModalSolicitudEnviada } from "../components/modals/ModalSolicitudEnviada";
import { getImageUri } from "../core/utils/imageLoad";
import { useServiceRegistrationWizard } from "../state/service-registration-store";
import { useAuthSessionContext } from "../components/routing/auth-session-store";
import { useFetch } from "../hooks/useFetch";
import {
	clearPendingProviderCategories,
	getPendingProviderCategories,
} from "../core/registration/provider-categories-pending";
import {
	resolveProviderReviewsCount,
	resolveProviderStats,
} from "../core/utils/providerStats";

// Importamos el hook refactorizado
import {
	useAssignedBadges,
	useCreateFavorite,
	useFavoritesByClient,
	useProvider,
	useUpdateFavorite,
} from "../hooks/control";
import { useFaqsByProvider } from "../hooks/services";

const buildFallbackRequestDescription = (serviceNames: string[]) => {
	const normalizedNames = serviceNames
		.map((name) => name.trim())
		.filter((name) => name.length > 0);
	if (normalizedNames.length === 0) return "servicio";
	return `servicio ${normalizedNames.join(", ")}`;
};

const normalizeCategoryLabel = (value: string) =>
	value
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.replace(/\s+/g, " ")
		.trim();

const dedupeCategoryLabels = (values: Array<string | null | undefined>) => {
	const seen = new Set<string>();
	const result: string[] = [];

	for (const value of values) {
		const label = String(value ?? "").trim();
		if (!label) continue;
		const key = normalizeCategoryLabel(label);
		if (!key || seen.has(key)) continue;
		seen.add(key);
		result.push(label);
	}

	return result;
};

const resolveRequestUrgency = (value: unknown): "Normal" | "Urgente" | null => {
	const normalized = String(value ?? "")
		.trim()
		.toLowerCase();

	if (normalized === "normal") return "Normal";
	if (normalized === "urgente") return "Urgente";
	return null;
};

export const ProviderInfo = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const { session, role } = useAuthSessionContext();
	const isProviderSession =
		role === "Proveedor" || session?.user?.type_user === "Proveedor";

	type NavigationProviderState = {
		id?: number;
		providerId?: number;
	};
	type NavigationState = {
		provider?: NavigationProviderState;
		openHireModal?: boolean;
	};
	type ClientAddressesResponse = {
		addresses?: Array<{
			id?: number | null;
			name_address?: string;
			street_address?: string;
			reference_address?: string;
		}>;
	};
	type CreateRequestResponse = {
		id?: number;
		request?: { id?: number };
	};
	type PendingOrderSubmission = {
		selectedItems: Array<{
			id: number;
			name: string;
			price: number;
			quantity: number;
		}>;
		selectedAddressId: number;
		subtotal: number;
		requestDescription: string;
		requestUrgency: "urgente" | "normal" | "";
	};
	type RequestContextOverride = {
		category?: string;
		subcategory?: string[];
		location?: {
			nombreDireccion?: string;
			direccion?: string;
			referencias?: string;
		};
	};
	type ProviderServiceCategoryLink = {
		categories_providers?: {
			categories_services?: {
				name_category?: string;
				subcategories_services?: Array<{ name_subcategory?: string }>;
			};
		};
		subcategories_services_providers?: Array<{
			subcategories_services?: { name_subcategory?: string };
		}>;
	};
	type ProviderServiceRecord = {
		id: number;
		name_service: string;
		base_price: number | string | null;
		description_service?: string | null;
		includes_tools?: string | null;
		not_includes_tools?: string | null;
		guarantee_days?: number | null;
		estimated_duration?: number | null;
		pictures_service?: string[] | null;
		is_active?: boolean | null;
		categories_services_providers?:
			| ProviderServiceCategoryLink[]
			| ProviderServiceCategoryLink;
	};
	type AlertState = {
		open: boolean;
		title: string;
		message: string;
		variant: AlertVariant;
	};

	// =========================================================================
	// 1. OBTENCIÓN DEL ID
	// =========================================================================
	const { id: urlId } = useParams<{ id: string }>();

	const navigationState = location.state as NavigationState | null;

	// Priorizamos la URL, luego el estado de navegación
	const providerIdRaw =
		urlId ?? navigationState?.provider?.id ?? navigationState?.provider?.providerId;

	// Si no hay ID válido, usamos 0 para no romper el hook (se puede manejar el error en UI)
	const providerId = providerIdRaw ? Number(providerIdRaw) : 0;
	const openHireModal = Boolean(navigationState?.openHireModal);

	// =========================================================================
	// 2. FETCH DE DATOS (Usando el Hook)
	// =========================================================================
	// El hook se encarga automáticamente de los estados loading y error.
	const {
		data: providerData,
		loading: isProviderLoading,
		execute: refetchProvider,
	} = useProvider(providerId);
	const { data: providerFaqData } = useFaqsByProvider(
		providerId,
		Boolean(session?.user?.id && providerId > 0),
	);
	const { data: assignedBadgesData } = useAssignedBadges(providerId);
	const { execute: executeGet } = useFetch<unknown>({
		endpoint: "/users/1",
		method: "GET",
		auto: false,
	});
	const { execute: executePostAddress } = useFetch<
		Record<string, unknown>,
		Record<string, unknown>
	>({
		endpoint: "/addresses",
		method: "POST",
		auto: false,
	});
	const { execute: executePatchAddressStatus } = useFetch<
		Record<string, unknown>,
		Record<string, unknown>
	>({
		endpoint: "/addresses/1/reactivate",
		method: "PATCH",
		auto: false,
	});
	const { execute: executePostRequest } = useFetch<
		CreateRequestResponse,
		Record<string, unknown>
	>({
		endpoint: "/requests",
		method: "POST",
		auto: false,
	});

	// =========================================================================
	// 3. ESTADOS DE UI Y NAVEGACIÓN
	// =========================================================================
	const [selectedCategory, setSelectedCategory] = useState<string>("Todos");
	const [selectedRanges, setSelectedRanges] = useState<string[]>([]);
	const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const category = params.get("category") || "Todos";
		const ranges = (params.get("ranges") || "")
			.split(",")
			.map((value) => value.trim())
			.filter(Boolean);
		const availability = (params.get("availability") || "")
			.split(",")
			.map((value) => value.trim())
			.filter(Boolean);

		setSelectedCategory(category);
		setSelectedRanges(ranges);
		setSelectedAvailability(availability);
	}, [location.search]);
	const [openOrderList, setOpenOrderList] = useState(false);
	const [showSentModal, setShowSentModal] = useState(false);
	const [galleryImages, setGalleryImages] = useState<string[]>([]);
	const [clientAddresses, setClientAddresses] = useState<AddressSummary[]>([]);
	const [loadingAddresses, setLoadingAddresses] = useState(false);
	const [clientProfileId, setClientProfileId] = useState<number | null>(null);
	const { serviceRequestData, updateServiceRequestData, resetServiceRequestData } =
		useServiceRegistrationWizard();
	const {
		data: favoriteList,
		execute: refreshFavorites,
		loading: isFavoritesLoading,
	} = useFavoritesByClient(clientProfileId ?? 0);
	const { create: createFavorite, loading: isCreateFavoriteLoading } =
		useCreateFavorite();
	const existingFavorite = useMemo(() => {
		if (!favoriteList?.length) return null;
		return (
			favoriteList.find((favorite) => favorite.providers?.id === providerId) ?? null
		);
	}, [favoriteList, providerId]);
	const { toggle: toggleFavorite, loading: isUpdateFavoriteLoading } =
		useUpdateFavorite(existingFavorite?.id ?? 0);
	const [alertState, setAlertState] = useState<AlertState>({
		open: false,
		title: "",
		message: "",
		variant: "info",
	});
	const [rehireWarningState, setRehireWarningState] = useState<{
		open: boolean;
		submission: PendingOrderSubmission | null;
		requestContextOverride?: RequestContextOverride;
	}>({
		open: false,
		submission: null,
		requestContextOverride: undefined,
	});
	const syncingPendingCategoriesRef = useRef<Set<number>>(new Set());
	const { execute: executePostCategoryProvider } = useFetch<
		unknown,
		Record<string, unknown>
	>({
		endpoint: "/categorie-provider",
		method: "POST",
		auto: false,
	});
	const showAlert = useCallback(
		({
			title,
			message,
			variant = "info",
		}: {
			title: string;
			message: string;
			variant?: AlertVariant;
		}) => {
			setAlertState({
				open: true,
				title,
				message,
				variant,
			});
		},
		[],
	);
	const handleCloseAlert = useCallback(() => {
		setAlertState((prev) => ({ ...prev, open: false }));
	}, []);

	const closeRehireWarningModal = useCallback(() => {
		setRehireWarningState({
			open: false,
			submission: null,
			requestContextOverride: undefined,
		});
	}, []);

	const closeOrderListModal = useCallback(() => {
		setOpenOrderList(false);
		resetServiceRequestData();
	}, [resetServiceRequestData]);

	const openOrderListModal = useCallback(() => {
		resetServiceRequestData();
		setOpenOrderList(true);
	}, [resetServiceRequestData]);

	const showProviderRequestRestrictionAlert = useCallback(() => {
		showAlert({
			variant: "warning",
			title: "Acceso restringido",
			message: "Tu perfil de proveedor no puede contratar ni solicitar servicios.",
		});
	}, [showAlert]);

	const toMediaUrl = (raw?: string | null) => {
		return getImageUri(raw) ?? "";
	};

	const shareUrl =
		typeof window !== "undefined"
			? `${window.location.origin}/proveedor/${providerId}`
			: undefined;

	const rangos = ["$0 - $30", "$50 - $80", "$80 - $100"];
	const disponibilidades = ["Hoy", "Esta Semana", "Este Mes"];

	const toProviderServiceRecord = useCallback(
		(value: unknown): ProviderServiceRecord | null => {
			if (!value || typeof value !== "object") return null;

			const candidate = value as Record<string, unknown>;
			const id = Number(candidate.id);
			const name_service = String(candidate.name_service ?? "").trim();

			if (!Number.isFinite(id) || id <= 0 || !name_service) return null;

			return {
				id,
				name_service,
				base_price:
					typeof candidate.base_price === "number" ||
					typeof candidate.base_price === "string"
						? candidate.base_price
						: null,
				description_service:
					typeof candidate.description_service === "string"
						? candidate.description_service
						: null,
				includes_tools:
					typeof candidate.includes_tools === "string"
						? candidate.includes_tools
						: null,
				not_includes_tools:
					typeof candidate.not_includes_tools === "string"
						? candidate.not_includes_tools
						: null,
				guarantee_days:
					typeof candidate.guarantee_days === "number"
						? candidate.guarantee_days
						: null,
				estimated_duration:
					typeof candidate.estimated_duration === "number"
						? candidate.estimated_duration
						: null,
				pictures_service: Array.isArray(candidate.pictures_service)
					? (candidate.pictures_service as string[])
					: null,
				is_active:
					typeof candidate.is_active === "boolean" ? candidate.is_active : null,
				categories_services_providers:
					(candidate.categories_services_providers as
						| ProviderServiceCategoryLink[]
						| ProviderServiceCategoryLink
						| undefined) ?? undefined,
			};
		},
		[],
	);

	const extractProviderServices = useCallback(
		(payload: unknown): ProviderServiceRecord[] => {
			if (Array.isArray(payload)) {
				return payload
					.map((entry) => toProviderServiceRecord(entry))
					.filter((entry): entry is ProviderServiceRecord => entry !== null);
			}

			if (!payload || typeof payload !== "object") return [];

			const wrapped = payload as {
				services?: unknown[];
				items?: unknown[];
				service_providers?: unknown[];
				"service-providers"?: unknown[];
				services_providers?: unknown[];
			};

			const collection =
				(Array.isArray(wrapped.services) && wrapped.services) ||
				(Array.isArray(wrapped.items) && wrapped.items) ||
				(Array.isArray(wrapped.service_providers) && wrapped.service_providers) ||
				(Array.isArray(wrapped["service-providers"]) &&
					wrapped["service-providers"]) ||
				(Array.isArray(wrapped.services_providers) &&
					wrapped.services_providers) ||
				[];

			return collection
				.map((entry) => toProviderServiceRecord(entry))
				.filter((entry): entry is ProviderServiceRecord => entry !== null);
		},
		[toProviderServiceRecord],
	);

	const providerServices = useMemo<ProviderServiceRecord[]>(() => {
		const services = extractProviderServices(providerData?.services_providers);
		const activeServices = services.filter((service) => service.is_active !== false);
		return activeServices.length > 0 ? activeServices : services;
	}, [extractProviderServices, providerData?.services_providers]);

	const extractServiceSubcategoryNames = useCallback(
		(service: ProviderServiceRecord): string[] => {
			const categoryLinks = Array.isArray(service.categories_services_providers)
				? service.categories_services_providers
				: service.categories_services_providers
					? [service.categories_services_providers]
					: [];

			const selectedSubcategories =
				categoryLinks
					?.flatMap(
						(link) =>
							link.subcategories_services_providers?.map((relation) =>
								relation.subcategories_services?.name_subcategory?.trim(),
							) ?? [],
					)
					.filter((name): name is string => Boolean(name)) ?? [];

			if (selectedSubcategories.length > 0) {
				return Array.from(new Set(selectedSubcategories));
			}

			const fallbackCategorySubcategories =
				categoryLinks
					?.flatMap(
						(link) =>
							link.categories_providers?.categories_services?.subcategories_services?.map(
								(sub) => sub.name_subcategory?.trim(),
							) ?? [],
					)
					.filter((name): name is string => Boolean(name)) ?? [];

			if (fallbackCategorySubcategories.length > 0) {
				return Array.from(new Set(fallbackCategorySubcategories));
			}

			const serviceName = service.name_service?.trim();
			return serviceName ? [serviceName] : [];
		},
		[],
	);

	useEffect(() => {
		const serviceImages = providerServices
			.flatMap((srv) =>
				Array.isArray(srv.pictures_service) ? srv.pictures_service : [],
			)
			.map((path) => toMediaUrl(path))
			.filter(Boolean);

		const portfolioImages = (providerData?.portfolio_providers ?? [])
			.flatMap((portfolio) =>
				Array.isArray(portfolio.pictures_portfolio)
					? portfolio.pictures_portfolio
					: [],
			)
			.map((path) => toMediaUrl(path))
			.filter(Boolean);

		const merged = Array.from(new Set([...serviceImages, ...portfolioImages]));
		setGalleryImages(merged);
	}, [providerData?.portfolio_providers, providerServices]);

	useEffect(() => {
		if (!providerId || !session?.user?.id || !providerData) return;

		const currentUserId = Number(session.user?.id ?? 0);
		const providerUserId = Number(providerData.users?.id ?? 0);
		if (!Number.isFinite(currentUserId) || currentUserId <= 0) return;
		if (!Number.isFinite(providerUserId) || providerUserId <= 0) return;
		if (providerUserId !== currentUserId) return;

		const pendingCategoryIds = getPendingProviderCategories(providerId);
		if (!pendingCategoryIds || pendingCategoryIds.length === 0) return;

		const assignedCategoryIds = new Set(
			(providerData.categories_providers ?? [])
				.map((assignment) => assignment?.categories_services?.id)
				.filter((id): id is number => typeof id === "number" && id > 0),
		);

		const missingCategoryIds = pendingCategoryIds.filter(
			(categoryId) => !assignedCategoryIds.has(categoryId),
		);

		if (missingCategoryIds.length === 0) {
			clearPendingProviderCategories(providerId);
			return;
		}

		if (syncingPendingCategoriesRef.current.has(providerId)) return;

		let cancelled = false;
		syncingPendingCategoriesRef.current.add(providerId);

		(async () => {
			try {
				await Promise.all(
					missingCategoryIds.map((categoryId) =>
						executePostCategoryProvider({
							endpoint: "/categorie-provider",
							method: "POST",
							body: {
								category_id: categoryId,
								provider_id: providerId,
							},
						}),
					),
				);

				if (cancelled) return;
				clearPendingProviderCategories(providerId);
				await refetchProvider({
					endpoint: `/providers/${providerId}`,
					method: "GET",
				});
			} catch {
				// Keep pending categories for next authenticated visit.
			} finally {
				syncingPendingCategoriesRef.current.delete(providerId);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [
		executePostCategoryProvider,
		providerData,
		providerId,
		refetchProvider,
		session?.user?.id,
	]);

	// Manejo del modal de contratación
	useEffect(() => {
		if (!openHireModal) return;
		if (isProviderSession) {
			showProviderRequestRestrictionAlert();
			return;
		}
		if (!session?.user?.id) {
			navigate("/login", {
				replace: false,
				state: {
					redirectTo: `/proveedor/${providerId}`,
					redirectState: {
						provider: { id: providerId },
						openHireModal: true,
					},
				},
			});
			return;
		}
		openOrderListModal();
	}, [
		navigate,
		openHireModal,
		providerId,
		isProviderSession,
		openOrderListModal,
		showProviderRequestRestrictionAlert,
	]);

	// Fetch client addresses when modal opens
	useEffect(() => {
		const userId = session?.user?.id;
		if (!openOrderList || !userId) return;
		let cancelled = false;
		const loadAddresses = async () => {
			setLoadingAddresses(true);
			try {
				const payload = await executeGet({
					endpoint: `/addresses/client/${userId}`,
					method: "GET",
				});
				const list = Array.isArray(payload)
					? payload
					: ((payload as ClientAddressesResponse)?.addresses ?? []);
				if (!cancelled) {
					setClientAddresses(
						list
							.filter(
								(
									a,
								): a is {
									id: number;
									name_address: string;
									street_address: string;
									reference_address?: string;
								} =>
									typeof a.id === "number" &&
									typeof a.name_address === "string" &&
									typeof a.street_address === "string",
							)
							.map((a) => ({
								id: a.id,
								name_address: a.name_address,
								street_address: a.street_address,
								reference_address: a.reference_address,
							})),
					);
				}
			} catch {
				if (!cancelled) setClientAddresses([]);
			} finally {
				if (!cancelled) setLoadingAddresses(false);
			}
		};
		void loadAddresses();
		return () => {
			cancelled = true;
		};
	}, [executeGet, openOrderList, session?.user?.id]);

	// Defensa adicional: nunca permitir el modal de contratación para cuentas proveedor.
	useEffect(() => {
		if (!openOrderList || !isProviderSession) return;
		closeOrderListModal();
		showProviderRequestRestrictionAlert();
	}, [
		openOrderList,
		isProviderSession,
		closeOrderListModal,
		showProviderRequestRestrictionAlert,
	]);

	// Handler to create a new address from the modal form
	const handleCreateAddress = async (data: NewAddressPayload) => {
		if (!session?.user?.id) return;
		const normalizedNameAddress = data.name_address.trim();
		const normalizedStreet = data.street_address.trim();
		const normalizedReference = data.reference_address.trim() || "N/A";
		const resolvedCity = data.name_city.trim() || "No especificado";
		const resolvedState = data.name_state.trim() || "No especificado";
		const result = await executePostAddress({
			endpoint: "/addresses",
			method: "POST",
			body: {
				user_id: Number(session.user.id),
				name_country: "Venezuela",
				name_state: resolvedState,
				name_city: resolvedCity,
				name_address: normalizedNameAddress,
				street_address: normalizedStreet,
				reference_address: normalizedReference,
			},
		});
		// Backend returns toResource(address, 'address') → normalizer returns { address: { id, ... } }
		const created = (result?.address ?? result) as
			| {
					id?: number;
					name_address?: string;
					street_address?: string;
					reference_address?: string;
			  }
			| undefined;
		if (created && typeof created.id === "number") {
			await executePatchAddressStatus({
				endpoint: `/addresses/${created.id}/reactivate`,
				method: "PATCH",
				body: {},
			}).catch(() => null);

			const newAddr: AddressSummary = {
				id: created.id,
				name_address: created.name_address ?? normalizedNameAddress,
				street_address: created.street_address ?? normalizedStreet,
				reference_address: created.reference_address ?? normalizedReference,
			};
			setClientAddresses((prev) => [...prev, newAddr]);
		}
	};

	// =========================================================================
	// 5. TRANSFORMACIÓN DE DATOS (Mappers Reactivos)
	// =========================================================================

	// Servicios derivados automáticamente de providerData
	const services = useMemo<OrderListService[]>(() => {
		if (!providerServices.length) return [];

		return providerServices.map((srv) => {
			const categoryLinks = Array.isArray(srv.categories_services_providers)
				? srv.categories_services_providers
				: srv.categories_services_providers
					? [srv.categories_services_providers]
					: [];
			const subcategoryNames = extractServiceSubcategoryNames(srv);
			return {
				id: srv.id,
				name: srv.name_service,
				category:
					categoryLinks[0]?.categories_providers?.categories_services
						?.name_category ?? "Servicio",
				subcategory: subcategoryNames.join(", ") || srv.name_service,
				price: Number(srv.base_price),
				quantity: 0,
				description: srv.description_service ?? undefined,
				includes: srv.includes_tools
					? srv.includes_tools.split("\n").filter(Boolean)
					: [],
				excludes: srv.not_includes_tools
					? srv.not_includes_tools.split("\n").filter(Boolean)
					: [],
				warrantyDays: srv.guarantee_days ?? null,
				estimatedDuration: srv.estimated_duration ?? null,
			};
		});
	}, [extractServiceSubcategoryNames, providerServices]);

	const fullServices = useMemo<
		Array<{
			id: number;
			name_service: string;
			base_price: number | null;
			description_service?: string | null;
		}>
	>(() => {
		if (!providerServices.length) return [];
		return providerServices.map((srv) => ({
			id: srv.id,
			name_service: srv.name_service,
			base_price: Number.isFinite(Number(srv.base_price))
				? Number(srv.base_price)
				: null,
			description_service: srv.description_service ?? null,
		}));
	}, [providerServices]);

	const serviceContextById = useMemo<
		Record<number, { category: string; subcategories: string[] }>
	>(() => {
		if (!providerServices.length) return {};

		return providerServices.reduce<
			Record<number, { category: string; subcategories: string[] }>
		>((acc, service) => {
			const categoryLinks = Array.isArray(service.categories_services_providers)
				? service.categories_services_providers
				: service.categories_services_providers
					? [service.categories_services_providers]
					: [];
			const category =
				categoryLinks[0]?.categories_providers?.categories_services?.name_category?.trim() ||
				"Servicio";
			const subcategoryNames = extractServiceSubcategoryNames(service);
			acc[service.id] = {
				category,
				subcategories:
					subcategoryNames.length > 0
						? subcategoryNames
						: [service.name_service],
			};
			return acc;
		}, {});
	}, [extractServiceSubcategoryNames, providerServices]);

	// Información formateada para las tarjetas
	const providerDisplayInfo = useMemo(() => {
		if (!providerData) return undefined;

		const faqItems = (providerFaqData ?? []).map((item) => ({
			id: item.id,
			question: item.question,
			answer: item.answer,
			category: item.categories_services?.name_category?.trim() || undefined,
		}));

		const providerAddresses = Array.isArray(providerData.users?.addresses)
			? providerData.users.addresses
			: [];
		const locationStr = (() => {
			const firstAddressWithLocation = providerAddresses.find((address) => {
				const city = address.name_city?.trim();
				const state = address.name_state?.trim();
				const street = address.street_address?.trim();
				const label = address.name_address?.trim();

				return Boolean(city || state || street || label);
			});

			if (!firstAddressWithLocation) return "Ubicación no especificada";

			const city = firstAddressWithLocation.name_city?.trim() || "";
			const state = firstAddressWithLocation.name_state?.trim() || "";
			const administrativeParts = [city, state].filter(
				(segment) => segment.length > 0,
			);

			if (administrativeParts.length > 0) {
				return administrativeParts.join(", ");
			}

			const street = firstAddressWithLocation.street_address?.trim();
			if (street) return street;

			const label = firstAddressWithLocation.name_address?.trim();
			return label && label.length > 0 ? label : "Ubicación no especificada";
		})();

		const displayName =
			providerData.company_name ||
			`${providerData.users?.names} ${providerData.users?.last_names}`;

		const stats = resolveProviderStats(providerData);
		const badgesSource =
			assignedBadgesData && assignedBadgesData.length > 0
				? assignedBadgesData.map((assignment) => assignment.badges)
				: (providerData.badges_providers || []).map(
						(assignment) => assignment.badges,
					);

		const badges = badgesSource.filter(Boolean).map((badge) => ({
			id: badge.id,
			name_badge: badge.name_badge,
			description_badge: badge.description_badge,
			icon_badge: toMediaUrl(badge.icon_badge),
			type_badge: badge.type_badge,
		}));

		const providerCategories = Array.from(
			new Map(
				(providerData.categories_providers ?? [])
					.map((assignment) => assignment?.categories_services)
					.filter(
						(category): category is NonNullable<typeof category> =>
							typeof category?.name_category === "string" &&
							category.name_category.trim().length > 0,
					)
					.map((category) => [
						category.id,
						{
							name: category.name_category.trim(),
							icon:
								toMediaUrl(
									category.icon_category ??
										category.picture_category ??
										null,
								) || null,
						},
					]),
			).values(),
		);
		const firstServiceCategoryLinks = Array.isArray(
			providerServices[0]?.categories_services_providers,
		)
			? providerServices[0]?.categories_services_providers
			: providerServices[0]?.categories_services_providers
				? [providerServices[0].categories_services_providers]
				: [];
		const fallbackServiceCategory =
			firstServiceCategoryLinks[0]?.categories_providers?.categories_services;

		// Permitir múltiples categorías si existen
		let categoryName: string | string[] = "General";
		if (
			Array.isArray(providerData.categories_providers) &&
			providerData.categories_providers.length > 0
		) {
			const allCategories = dedupeCategoryLabels(
				providerData.categories_providers.map((assignment) =>
					assignment?.categories_services?.name_category?.trim(),
				),
			);
			if (allCategories.length > 1) {
				categoryName = allCategories;
			} else if (allCategories.length === 1) {
				categoryName = allCategories[0];
			}
		} else if (fallbackServiceCategory?.name_category?.trim()) {
			categoryName = fallbackServiceCategory.name_category.trim();
		}
		const categoryIcon = providerCategories[0]?.icon || undefined;

		// Avatar URL extraction (use image_profile only, as photo does not exist on type)
		const avatarRaw = providerData.users?.image_profile;
		const avatarUrl = avatarRaw ? toMediaUrl(avatarRaw) : undefined;

		return {
			id: providerData.id,
			name: displayName,
			description:
				providerData.description_provider || "Sin descripción disponible.",
			rating: providerData.rating,
			reviews: resolveProviderReviewsCount(providerData),
			location: locationStr,
			status: providerData.availability ? "ACTIVO" : "NO DISPONIBLE",
			languages: providerData.languages_provider,
			categories: providerCategories,
			category: categoryName,
			categoryIcon,
			experienceYears: providerData.experience_years ?? 0,
			reviewsList: providerData.users?.reviews_qualified || [],
			badges,
			faq: faqItems,
			stats,
			avatarUrl,
		};
	}, [assignedBadgesData, providerData, providerFaqData, providerServices]);

	// Precio más bajo derivado de los servicios
	const cheapestPrice = useMemo(() => {
		if (!services.length) return undefined;
		const values = services
			.map((service) => service.price)
			.filter((price) => Number.isFinite(price) && price > 0);

		if (values.length === 0) return undefined;
		return Math.min(...values);
	}, [services]);

	// Helpers de navegación
	const goToProvidersGeneral = (
		category: string,
		ranges: string[],
		availability: string[],
	) => {
		const params = new URLSearchParams();
		if (category && category !== "Todos") params.set("category", category);
		if (ranges.length) params.set("ranges", ranges.join(","));
		if (availability.length) params.set("availability", availability.join(","));
		const query = params.toString();
		navigate(`/proveedores/general${query ? `?${query}` : ""}`);
	};

	const resolveClientProfileId = useCallback(async () => {
		if (!session?.user?.id) return null;
		const userPayload = await executeGet({
			endpoint: `/users/${session.user.id}`,
			method: "GET",
		});
		const userPayloadRecord = (userPayload ?? {}) as Record<string, unknown>;
		const nestedUser = userPayloadRecord.user;
		const userData =
			nestedUser && typeof nestedUser === "object"
				? (nestedUser as Record<string, unknown>)
				: userPayloadRecord;
		const clients = userData?.clients as { id?: number } | undefined;
		return typeof clients?.id === "number" ? clients.id : null;
	}, [session?.user?.id, executeGet]);

	useEffect(() => {
		let cancelled = false;

		const syncClientProfileId = async () => {
			if (!session?.user?.id) {
				if (!cancelled) setClientProfileId(null);
				return;
			}

			try {
				const id = await resolveClientProfileId();
				if (!cancelled) setClientProfileId(id);
			} catch {
				if (!cancelled) setClientProfileId(null);
			}
		};

		void syncClientProfileId();

		return () => {
			cancelled = true;
		};
	}, [resolveClientProfileId, session?.user?.id]);

	const isFavoriteProvider = Boolean(existingFavorite?.is_favorite);
	const isFavoriteActionLoading =
		isFavoritesLoading || isCreateFavoriteLoading || isUpdateFavoriteLoading;

	const handleToggleFavorite = useCallback(async () => {
		if (!providerId) return;

		if (!session?.user?.id) {
			navigate("/login", {
				replace: false,
				state: {
					redirectTo: `/proveedor/${providerId}`,
					redirectState: {
						provider: { id: providerId },
					},
				},
			});
			return;
		}

		try {
			let ensuredClientId = clientProfileId;
			if (!ensuredClientId) {
				ensuredClientId = await resolveClientProfileId();
				setClientProfileId(ensuredClientId);
			}

			if (!ensuredClientId) {
				showAlert({
					variant: "error",
					title: "Error",
					message: "No se encontró el perfil de cliente asociado a tu cuenta.",
				});
				return;
			}

			if (existingFavorite?.id) {
				await toggleFavorite(!isFavoriteProvider);
			} else {
				await createFavorite({
					client_id: ensuredClientId,
					provider_id: providerId,
				});
			}

			await refreshFavorites({});
		} catch (error: unknown) {
			showAlert({
				variant: "error",
				title: "Favoritos",
				message:
					(error instanceof Error ? error.message : undefined) ||
					"No se pudo actualizar el estado de favoritos.",
			});
		}
	}, [
		createFavorite,
		clientProfileId,
		existingFavorite?.id,
		isFavoriteProvider,
		navigate,
		providerId,
		refreshFavorites,
		resolveClientProfileId,
		showAlert,
		session?.user?.id,
		toggleFavorite,
	]);

	const resolveClientId = useCallback(async () => {
		return resolveClientProfileId();
	}, [resolveClientProfileId]);

	const requestContextForModal = useMemo<ServiceContextForm>(
		() => ({
			urgency: serviceRequestData.urgency,
			date: serviceRequestData.schedule.date,
			time: serviceRequestData.schedule.time,
			period: serviceRequestData.schedule.period,
			category: serviceRequestData.category,
			subcategory: serviceRequestData.subcategory,
			addressName: serviceRequestData.location.nombreDireccion,
			references: serviceRequestData.location.referencias,
		}),
		[serviceRequestData],
	);

	const submitOrderRequest = useCallback(
		async (
			{
				selectedItems,
				selectedAddressId,
				subtotal,
				requestDescription,
				requestUrgency,
			}: PendingOrderSubmission,
			requestContextOverride?: RequestContextOverride,
			options?: { skipRehireWarning?: boolean },
		) => {
			if (isProviderSession) {
				closeOrderListModal();
				showProviderRequestRestrictionAlert();
				return;
			}

			if (!session?.user?.id) {
				closeOrderListModal();
				navigate("/login", {
					replace: false,
					state: {
						redirectTo: `/proveedor/${providerId}`,
						redirectState: {
							provider: { id: providerId },
							openHireModal: true,
						},
					},
				});
				return;
			}

			if (selectedItems.length === 0) {
				showAlert({
					variant: "warning",
					title: "Sin servicios",
					message: "Selecciona al menos un servicio antes de solicitar.",
				});
				return;
			}

			try {
				const to24Hour = (time: string, period: "AM" | "PM") => {
					const [hoursRaw = "0", minutes = "00"] = time.split(":");
					const parsedHours = Number(hoursRaw);
					if (!Number.isFinite(parsedHours)) return time;
					const safeHours = parsedHours % 12;
					const hours24 = period === "PM" ? safeHours + 12 : safeHours;
					return `${String(hours24).padStart(2, "0")}:${minutes}`;
				};

				const clientId = await resolveClientId();

				if (!clientId) {
					showAlert({
						variant: "error",
						title: "Error",
						message:
							"No se encontró el perfil de cliente asociado a tu cuenta.",
					});
					return;
				}

				const extractRows = (
					payload: unknown,
				): Array<Record<string, unknown>> => {
					if (Array.isArray(payload)) {
						return payload.filter((row): row is Record<string, unknown> =>
							Boolean(row && typeof row === "object"),
						);
					}
					if (!payload || typeof payload !== "object") return [];
					const candidate = payload as Record<string, unknown>;
					const list =
						(Array.isArray(candidate.items) && candidate.items) ||
						(Array.isArray(candidate.requests) && candidate.requests) ||
						(Array.isArray(candidate.orders) && candidate.orders) ||
						(Array.isArray(candidate.data) && candidate.data) ||
						[];
					return list.filter((row): row is Record<string, unknown> =>
						Boolean(row && typeof row === "object"),
					);
				};

				const [clientRequestsPayload, clientOrdersPayload] = await Promise.all([
					executeGet({
						endpoint: `/requests/client/${clientId}?page=1&pageSize=200`,
						method: "GET",
					}),
					executeGet({
						endpoint: `/orders/client/${clientId}?page=1&pageSize=200`,
						method: "GET",
					}),
				]);

				const requestRows = extractRows(clientRequestsPayload);
				const orderRows = extractRows(clientOrdersPayload);

				const activeOrderRequestIds = new Set<number>();
				const terminalOrderRequestIds = new Set<number>();
				let hasActiveOrderWithProvider = false;

				for (const orderRow of orderRows) {
					const providerData = orderRow.providers as
						| Record<string, unknown>
						| undefined;
					const orderProviderId = Number(
						providerData?.id ?? orderRow.provider_id ?? 0,
					);
					if (
						!Number.isFinite(orderProviderId) ||
						orderProviderId !== providerId
					) {
						continue;
					}

					const status = String(orderRow.status_order ?? "")
						.toLowerCase()
						.trim();
					const isTerminalStatus =
						status.includes("complet") ||
						status.includes("cancel") ||
						status.includes("pagad");

					const orderRequest = orderRow.requests as
						| Record<string, unknown>
						| undefined;
					const orderRequestId = Number(
						orderRequest?.id ?? orderRow.request_id ?? 0,
					);
					if (Number.isFinite(orderRequestId) && orderRequestId > 0) {
						if (isTerminalStatus) {
							terminalOrderRequestIds.add(orderRequestId);
						} else {
							activeOrderRequestIds.add(orderRequestId);
						}
					}

					if (!isTerminalStatus) {
						hasActiveOrderWithProvider = true;
					}
				}

				const hasActiveRequestWithProvider = requestRows.some((requestRow) => {
					const serviceProvider = requestRow.services_providers as
						| Record<string, unknown>
						| undefined;
					const requestProviderId = Number(
						serviceProvider?.provider_id ?? requestRow.provider_id ?? 0,
					);
					if (
						!Number.isFinite(requestProviderId) ||
						requestProviderId !== providerId
					) {
						return false;
					}

					const status = String(requestRow.status_request ?? "")
						.toLowerCase()
						.trim();
					if (status.includes("pend")) return true;

					if (status.includes("acept")) {
						const requestId = Number(
							requestRow.id ?? requestRow.request_id ?? 0,
						);
						if (Number.isFinite(requestId) && requestId > 0) {
							if (terminalOrderRequestIds.has(requestId)) return false;
							if (activeOrderRequestIds.has(requestId)) return true;
						}
						// Solicitud aceptada sin pedido asociado aún: se considera activa.
						return true;
					}

					return false;
				});

				if (
					(hasActiveRequestWithProvider || hasActiveOrderWithProvider) &&
					!options?.skipRehireWarning
				) {
					setRehireWarningState({
						open: true,
						submission: {
							selectedItems: selectedItems.map((item) => ({ ...item })),
							selectedAddressId,
							subtotal,
							requestDescription,
							requestUrgency,
						},
						requestContextOverride: requestContextOverride
							? {
									category: requestContextOverride.category,
									subcategory: requestContextOverride.subcategory
										? [...requestContextOverride.subcategory]
										: undefined,
									location: requestContextOverride.location
										? { ...requestContextOverride.location }
										: undefined,
								}
							: undefined,
					});
					return;
				}

				const firstServiceId = selectedItems[0]?.id;
				if (!firstServiceId || typeof firstServiceId !== "number") {
					showAlert({
						variant: "error",
						title: "Error",
						message: "No se pudo determinar el servicio seleccionado.",
					});
					return;
				}

				const resolvedUrgency =
					resolveRequestUrgency(requestUrgency) ??
					resolveRequestUrgency(serviceRequestData.urgency);

				if (!resolvedUrgency) {
					showAlert({
						variant: "warning",
						title: "Urgencia requerida",
						message: "Selecciona si la solicitud es normal o urgente.",
					});
					return;
				}
				const fallbackDateTime = new Date();
				const selectedDate = serviceRequestData.schedule.date;
				const selectedTime = serviceRequestData.schedule.time;
				const selectedPeriod = serviceRequestData.schedule.period || "AM";
				const selectedTime24 = selectedTime
					? to24Hour(selectedTime, selectedPeriod)
					: fallbackDateTime.toTimeString().slice(0, 5);
				const selectedHourLabel = selectedTime
					? `${selectedTime} ${selectedPeriod}`
					: fallbackDateTime.toTimeString().slice(0, 5);
				const composedDeadline = selectedDate
					? new Date(`${selectedDate}T${selectedTime24}:00`)
					: new Date(Date.now() + 7 * 24 * 3600 * 1000);

				const itemDetails = selectedItems.map((item) => ({
					name: item.name,
					quantity: item.quantity,
					unitPrice: item.price,
					lineTotal: item.quantity * item.price,
				}));
				const effectiveRequestDescription =
					requestDescription.trim() ||
					buildFallbackRequestDescription(
						selectedItems.map((item) => item.name),
					);

				const effectiveCategory =
					requestContextOverride?.category || serviceRequestData.category;

				const body = {
					client_id: Number(clientId),
					service_provider_id: Number(firstServiceId),
					address_id: Number(selectedAddressId),
					title_request: `Solicitud de ${effectiveCategory || providerDisplayInfo?.name || "servicio"}`,
					description_request: effectiveRequestDescription,
					minimun_budget: Math.max(Number(subtotal), 1),
					maximun_budget: Math.max(Number(subtotal), 1),
					urgency_request: resolvedUrgency,
					date_request: new Date().toISOString(),
					hour_request: selectedHourLabel,
					deadline_request: composedDeadline.toISOString(),
				};

				const result = await executePostRequest({
					endpoint: "/requests",
					method: "POST",
					body: body as Record<string, unknown>,
				});
				const backendRequestId = result?.request?.id ?? result?.id ?? null;

				try {
					const newOrder = {
						id: backendRequestId
							? `REQ-${backendRequestId}`
							: `LOCAL-${Date.now()}`,
						requestId: backendRequestId,
						providerId,
						providerName: providerDisplayInfo?.name,
						clientId: session?.user?.id,
						client:
							session?.user?.name?.trim() ||
							session?.user?.email ||
							"Cliente",
						items: selectedItems,
						subtotal,
						date: new Date().toISOString(),
						status: "Dialogando",
						title: body.title_request,
						description: body.description_request,
						urgency: serviceRequestData.urgency,
						requestItems: itemDetails,
						requestDate: selectedDate,
						requestTime: selectedHourLabel,
						serviceRequestData,
					};
					const existing = JSON.parse(
						localStorage.getItem("local_orders") || "[]",
					);
					existing.push(newOrder);
					localStorage.setItem("local_orders", JSON.stringify(existing));
				} catch {
					// Non-critical — ignore localStorage failures
				}

				closeOrderListModal();
				setShowSentModal(true);
			} catch (err) {
				const message =
					err instanceof Error
						? err.message
						: "Error desconocido al crear la solicitud";
				showAlert({
					variant: "error",
					title: "No se pudo enviar la solicitud",
					message,
				});
			}
		},
		[
			serviceRequestData,
			navigate,
			providerDisplayInfo?.name,
			providerId,
			resolveClientId,
			showAlert,
			showProviderRequestRestrictionAlert,
			closeOrderListModal,
			isProviderSession,
			session?.user?.email,
			session?.user?.id,
			session?.user?.name,
			executeGet,
			executePostRequest,
		],
	);

	// =========================================================================
	// 6. RENDERIZADO
	// =========================================================================
	return (
		<Layout>
			<div className='flex flex-1 bg-slate-50'>
				<Sidebar
					ranges={rangos}
					availability={disponibilidades}
					selectedCategory={selectedCategory}
					onSelect={(label) => {
						setSelectedCategory(label);
						goToProvidersGeneral(label, selectedRanges, selectedAvailability);
					}}
					selectedRanges={selectedRanges}
					onRangeChange={(range, checked) => {
						const nextRanges = checked
							? [...selectedRanges, range]
							: selectedRanges.filter((r) => r !== range);
						setSelectedRanges(nextRanges);
						goToProvidersGeneral(
							selectedCategory,
							nextRanges,
							selectedAvailability,
						);
					}}
					selectedAvailability={selectedAvailability}
					onAvailabilityChange={(label, checked) => {
						const nextAvailability = checked
							? [...selectedAvailability, label]
							: selectedAvailability.filter((r) => r !== label);
						setSelectedAvailability(nextAvailability);
						goToProvidersGeneral(
							selectedCategory,
							selectedRanges,
							nextAvailability,
						);
					}}
				/>

				<main className='flex-1 min-w-0 px-4 py-6 md:px-6 lg:px-8'>
					<div className='mx-auto w-full max-w-7xl'>
						<button
							type='button'
							onClick={() =>
								goToProvidersGeneral(
									selectedCategory,
									selectedRanges,
									selectedAvailability,
								)
							}
							className='mb-4 cursor-pointer text-sm font-semibold text-slate-700 transition hover:text-primary'
						>
							&lt; Volver a todos los Servicios
						</button>

						{isProviderLoading && !providerData ? (
							<div className='flex justify-center items-center h-64 text-slate-500'>
								<div className='animate-pulse text-lg font-semibold text-primary'>
									Cargando información del proveedor...
								</div>
							</div>
						) : !providerData ? (
							<div className='flex justify-center items-center h-64 text-slate-500'>
								<div className='text-lg font-semibold'>
									Proveedor no encontrado o no disponible.
								</div>
							</div>
						) : (
							<div className='grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px]'>
								<div className='min-w-0 lg:col-start-1 lg:row-start-1'>
									<ProviderMainCard
										name={providerDisplayInfo?.name}
										description={providerDisplayInfo?.description}
										rating={providerDisplayInfo?.rating}
										reviews={providerDisplayInfo?.reviews}
										location={providerDisplayInfo?.location}
										shareUrl={shareUrl}
										stats={providerDisplayInfo?.stats}
										experience={providerDisplayInfo?.experienceYears}
										isFavorite={isFavoriteProvider}
										favoriteLoading={isFavoriteActionLoading}
										onToggleFavorite={handleToggleFavorite}
										avatarUrl={providerDisplayInfo?.avatarUrl}
									/>
								</div>

								<div className='lg:col-start-2 lg:row-span-2 lg:self-start'>
									<ProviderSideCard
										location={providerDisplayInfo?.location}
										responseTime={
											providerDisplayInfo?.stats?.responseTime
										}
										priceFrom={
											cheapestPrice
												? `$${cheapestPrice.toLocaleString(
														"es-VE",
														{
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														},
													)}`
												: "Consultar"
										}
										category={
											providerDisplayInfo?.category as
												| string
												| string[]
										}
										categoryIcon={providerDisplayInfo?.categoryIcon}
										categories={providerDisplayInfo?.categories}
										onHire={() => {
											if (isProviderSession) {
												showProviderRequestRestrictionAlert();
												return;
											}
											if (!session?.user?.id) {
												navigate("/login", {
													replace: false,
													state: {
														redirectTo: `/proveedor/${providerId}`,
														redirectState: {
															provider: { id: providerId },
															openHireModal: true,
														},
													},
												});
												return;
											}
											openOrderListModal();
										}}
										schedules={providerData?.schedules_providers}
									/>
								</div>

								<div className='min-w-0 lg:col-start-1 lg:row-start-2'>
									<ProviderDetails
										name={providerDisplayInfo?.name}
										description={providerDisplayInfo?.description}
										category={
											providerDisplayInfo?.category as
												| string
												| string[]
										}
										categoryIcon={providerDisplayInfo?.categoryIcon}
										categories={providerDisplayInfo?.categories}
										rating={providerDisplayInfo?.rating}
										reviews={providerDisplayInfo?.reviews}
										experience={providerDisplayInfo?.experienceYears}
										faq={providerDisplayInfo?.faq}
										badges={providerDisplayInfo?.badges}
										languages={providerDisplayInfo?.languages}
										reviewsList={
											providerDisplayInfo?.reviewsList || []
										}
										services={fullServices}
										galleryImages={galleryImages}
									/>
								</div>
							</div>
						)}
					</div>
				</main>
			</div>

			{/* Modal que usa la data reactiva calculada */}
			<OrderListModal
				open={openOrderList}
				onClose={closeOrderListModal}
				services={services}
				provider={providerDisplayInfo}
				addresses={clientAddresses}
				loadingAddresses={loadingAddresses}
				onCreateAddress={handleCreateAddress}
				requireServiceContext
				serviceContext={requestContextForModal}
				onServiceContextChange={(patch) => {
					updateServiceRequestData({
						...(patch.urgency !== undefined
							? { urgency: patch.urgency }
							: {}),
						...(patch.category !== undefined
							? { category: patch.category }
							: {}),
						...(patch.subcategory !== undefined
							? { subcategory: patch.subcategory }
							: {}),
						...(patch.addressName !== undefined ||
						patch.references !== undefined
							? {
									location: {
										direccion: serviceRequestData.location.direccion,
										nombreDireccion:
											patch.addressName ??
											serviceRequestData.location.nombreDireccion,
										referencias:
											patch.references ??
											serviceRequestData.location.referencias,
									},
								}
							: {}),
						...(patch.date !== undefined ||
						patch.time !== undefined ||
						patch.period !== undefined
							? {
									schedule: {
										date:
											patch.date ??
											serviceRequestData.schedule.date,
										time:
											patch.time ??
											serviceRequestData.schedule.time,
										period:
											patch.period ??
											serviceRequestData.schedule.period,
									},
								}
							: {}),
					});
				}}
				onSubmit={async ({
					items,
					selectedAddressId,
					requestDescription,
				}: OrderListSubmitPayload) => {
					if (isProviderSession) {
						closeOrderListModal();
						showProviderRequestRestrictionAlert();
						return;
					}
					if (!session?.user?.id) {
						closeOrderListModal();
						navigate("/login", {
							replace: false,
							state: {
								redirectTo: `/proveedor/${providerId}`,
								redirectState: {
									provider: { id: providerId },
									openHireModal: true,
								},
							},
						});
						return;
					}

					const selectedItems = items.filter((i) => (i.quantity ?? 0) > 0);
					if (selectedItems.length === 0) {
						showAlert({
							variant: "warning",
							title: "Sin servicios",
							message:
								"Selecciona al menos un servicio antes de solicitar.",
						});
						return;
					}

					if (!selectedAddressId) {
						showAlert({
							variant: "error",
							title: "Sin dirección",
							message:
								"Selecciona o agrega una dirección antes de enviar la solicitud.",
						});
						return;
					}

					const selectedAddress = clientAddresses.find(
						(address) => address.id === selectedAddressId,
					);

					const selectedItemServiceContext = selectedItems
						.map((item) => serviceContextById[Number(item.id)])
						.filter(
							(
								context,
							): context is {
								category: string;
								subcategories: string[];
							} => Boolean(context),
						);

					const derivedCategory =
						selectedItemServiceContext[0]?.category?.trim() ||
						serviceRequestData.category;
					const derivedSubcategory = Array.from(
						new Set(
							selectedItemServiceContext
								.flatMap((context) => context.subcategories)
								.map((subcategory) => subcategory?.trim())
								.filter((subcategory): subcategory is string =>
									Boolean(subcategory),
								),
						),
					);

					const derivedLocation = {
						direccion:
							selectedAddress?.street_address ||
							serviceRequestData.location.direccion,
						nombreDireccion:
							selectedAddress?.name_address ||
							serviceRequestData.location.nombreDireccion,
						referencias:
							selectedAddress?.reference_address ||
							serviceRequestData.location.referencias,
					};

					updateServiceRequestData({
						category: derivedCategory,
						subcategory:
							derivedSubcategory.length > 0
								? derivedSubcategory
								: serviceRequestData.subcategory,
						location: derivedLocation,
					});

					const subtotal = selectedItems.reduce(
						(s, it) => s + (it.price ?? 0) * (it.quantity ?? 0),
						0,
					);

					const normalizedPayload: PendingOrderSubmission = {
						selectedItems: selectedItems
							.filter(
								(item): item is typeof item & { id: number } =>
									typeof item.id === "number",
							)
							.map((item) => ({
								id: item.id,
								name: item.name,
								price: item.price,
								quantity: item.quantity ?? 0,
							})),
						selectedAddressId,
						subtotal,
						requestDescription:
							requestDescription.trim() ||
							buildFallbackRequestDescription(
								selectedItems.map((item) => item.name),
							),
						requestUrgency: serviceRequestData.urgency,
					};

					if (normalizedPayload.selectedItems.length === 0) {
						showAlert({
							variant: "error",
							title: "Error",
							message: "No se pudo determinar el servicio seleccionado.",
						});
						return;
					}

					const hasContextNow =
						Boolean(serviceRequestData.urgency) &&
						derivedCategory.trim().length > 0 &&
						(derivedSubcategory.length > 0 ||
							serviceRequestData.subcategory.length > 0) &&
						serviceRequestData.schedule.date.trim().length > 0 &&
						serviceRequestData.schedule.time.trim().length > 0 &&
						derivedLocation.direccion.trim().length > 0 &&
						derivedLocation.nombreDireccion.trim().length > 0;

					if (!hasContextNow) {
						showAlert({
							variant: "info",
							title: "Completa datos de solicitud",
							message:
								"Antes de contratar, completa urgencia, fecha, hora, categoría y subcategorías en este modal.",
						});
						return;
					}

					await submitOrderRequest(normalizedPayload, {
						category: derivedCategory,
						subcategory:
							derivedSubcategory.length > 0
								? derivedSubcategory
								: serviceRequestData.subcategory,
						location: derivedLocation,
					});
				}}
			/>
			<ModalSolicitudEnviada
				open={showSentModal}
				onClose={() => {
					setShowSentModal(false);
					navigate("/user-profile", {
						state: { initialTab: "orders" },
					});
				}}
			/>
			<ConfirmModal
				open={rehireWarningState.open}
				title='Pedido anterior detectado'
				message='Ya tienes una solicitud o servicio previo con este proveedor. Puedes enviar una nueva solicitud si deseas volver a contratarlo ahora.'
				variant='info'
				confirmLabel='Sí, enviar nueva solicitud'
				cancelLabel='Cancelar'
				onCancel={closeRehireWarningModal}
				onConfirm={async () => {
					const pendingSubmission = rehireWarningState.submission;
					const pendingContext = rehireWarningState.requestContextOverride;
					closeRehireWarningModal();

					if (!pendingSubmission) return;

					await submitOrderRequest(pendingSubmission, pendingContext, {
						skipRehireWarning: true,
					});
				}}
			/>
			<AlertModal
				open={alertState.open}
				onClose={handleCloseAlert}
				title={alertState.title}
				message={alertState.message}
				variant={alertState.variant}
			/>
		</Layout>
	);
};
